import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { UserContext, UserPayload } from "../../Context/userContext";
import agent from "../../API/apiCalls";
import authService from "../../utils/authService";
import {
    LOGIN_OTP_LENGTH,
    OTP_INVALID_MESSAGE,
    OTP_RESEND_FAILED_MESSAGE,
    OTP_RESENT_SUCCESS_MESSAGE,
    OTP_SEND_FAILED_MESSAGE,
    OTP_SEND_NETWORK_ERROR_MESSAGE,
    OTP_SENT_SUCCESS_MESSAGE,
    OTP_VERIFY_FAILED_MESSAGE,
    RESEND_OTP_COOLDOWN_SEC,
} from "./login.constants";
import { LoginFormErrors, LoginStep } from "./login.types";
import { validateLoginEmail, validateLoginOtp } from "./login.validation";
import axios from "axios";

const normalizeOtpDigits = (value: string) =>
    value.replace(/\D/g, "").slice(0, LOGIN_OTP_LENGTH);

export const useLogin = () => {
    const { setShowLoader, GetUserMe } = useContext(UserContext) as UserPayload;

    const [loginStep, setLoginStep] = useState<LoginStep>("email");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [otpFieldKey, setOtpFieldKey] = useState(0);
    const [formErrors, setFormErrors] = useState<LoginFormErrors>({});
    const [isPrimarySubmitting, setIsPrimarySubmitting] = useState(false);
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    const [resendCooldownSec, setResendCooldownSec] = useState(0);

    const isFormBusy = isPrimarySubmitting || isResendingOtp;

    const otpDigits = normalizeOtpDigits(otp);
    const isOtpComplete = otpDigits.length === LOGIN_OTP_LENGTH;

    useEffect(() => {
        if (resendCooldownSec <= 0) {
            return;
        }
        const timerId = window.setTimeout(() => {
            setResendCooldownSec((current) => Math.max(current - 1, 0));
        }, 1000);
        return () => window.clearTimeout(timerId);
    }, [resendCooldownSec]);

    const startResendCooldown = useCallback(() => {
        setResendCooldownSec(RESEND_OTP_COOLDOWN_SEC);
    }, []);

    const clearFieldError = useCallback((field: keyof LoginFormErrors) => {
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }, []);

    const resetOtpField = useCallback(() => {
        setOtp("");
        setOtpFieldKey((prev) => prev + 1);
    }, []);

    const handleOtpChange = useCallback((value: string) => {
        setOtp(normalizeOtpDigits(value));
    }, []);

    const requestLoginOtp = useCallback(async (): Promise<boolean> => {
        const trimmedEmail = email.trim();
        const response = await agent.SendLoginOtp.post(trimmedEmail);
        return Boolean(response?.flagStatus && response?.value);
    }, [email]);

    const handleSignInWithGoogle = useCallback(async () => {
        try {
            setShowLoader(true);
            const response = await agent.SignInWithGoogle.get();
            if (response?.flagStatus) {
                window.location.href = response.value;
            }
        } catch (error) {
            console.error("Google sign-in error:", error);
            setShowLoader(false);
        }
    }, [setShowLoader]);

    const handleSendLoginOtp = useCallback(async () => {
        const errors = validateLoginEmail(email);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setIsPrimarySubmitting(true);
        setFormErrors({});

        try {
            const sent = await requestLoginOtp();

            if (sent) {
                resetOtpField();
                setLoginStep("otp");
                startResendCooldown();
                toast.success(OTP_SENT_SUCCESS_MESSAGE);
            } else {
                toast.error(OTP_SEND_FAILED_MESSAGE);
                setFormErrors({ general: OTP_SEND_FAILED_MESSAGE });
            }
        } catch (error: unknown) {
            const isRequestError = axios.isAxiosError(error) && Boolean(error.response);
            const message = isRequestError
                ? OTP_SEND_FAILED_MESSAGE
                : OTP_SEND_NETWORK_ERROR_MESSAGE;

            toast.error(message);
            setFormErrors({ general: message });
        } finally {
            setIsPrimarySubmitting(false);
        }
    }, [email, requestLoginOtp, resetOtpField, startResendCooldown]);

    const handleVerifyLoginOtp = useCallback(async () => {
        if (!isOtpComplete) {
            return;
        }

        const errors = validateLoginOtp(otpDigits);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setIsPrimarySubmitting(true);
        setFormErrors({});

        try {
            const trimmedEmail = email.trim();
            const response = await agent.VerifyLoginOtp.post(trimmedEmail, otpDigits);

            if (response?.flagStatus && response?.value) {
                authService.setToken(response.value);
                await GetUserMe();
                return;
            }

            if (response?.error === "OtpValidationError") {
                setFormErrors({ otp: OTP_INVALID_MESSAGE });
            } else {
                setFormErrors({ otp: OTP_VERIFY_FAILED_MESSAGE });
            }
        } catch (error: unknown) {
            const isRequestError = axios.isAxiosError(error) && Boolean(error.response);

            setFormErrors({
                otp: isRequestError ? OTP_INVALID_MESSAGE : OTP_VERIFY_FAILED_MESSAGE,
            });
        } finally {
            setIsPrimarySubmitting(false);
        }
    }, [email, otpDigits, isOtpComplete, GetUserMe]);

    const handleBackToEmail = useCallback(() => {
        if (isFormBusy) {
            return;
        }

        setLoginStep("email");
        resetOtpField();
        setFormErrors({});
        setResendCooldownSec(0);
    }, [isFormBusy, resetOtpField]);

    const handleResendOtp = useCallback(async () => {
        if (resendCooldownSec > 0 || isFormBusy) {
            return;
        }

        setIsResendingOtp(true);
        setFormErrors({});

        try {
            const sent = await requestLoginOtp();

            if (sent) {
                resetOtpField();
                startResendCooldown();
                toast.success(OTP_RESENT_SUCCESS_MESSAGE);
            } else {
                toast.error(OTP_RESEND_FAILED_MESSAGE);
                setFormErrors({ otp: OTP_RESEND_FAILED_MESSAGE });
            }
        } catch {
            toast.error(OTP_RESEND_FAILED_MESSAGE);
            setFormErrors({ otp: OTP_RESEND_FAILED_MESSAGE });
        } finally {
            setIsResendingOtp(false);
        }
    }, [
        isFormBusy,
        requestLoginOtp,
        resendCooldownSec,
        resetOtpField,
        startResendCooldown,
    ]);

    const handlePrimaryAction = useCallback(() => {
        if (loginStep === "email") {
            handleSendLoginOtp();
        } else if (isOtpComplete) {
            handleVerifyLoginOtp();
        }
    }, [loginStep, isOtpComplete, handleSendLoginOtp, handleVerifyLoginOtp]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (isFormBusy) {
                return;
            }

            if (e.key === "Enter") {
                handlePrimaryAction();
            }
        },
        [handlePrimaryAction, isFormBusy]
    );

    const subheadingText =
        loginStep === "email"
            ? "Sign in to your Developer Portal"
            : "Enter the verification code sent to your email";

    const primaryButtonLabel = loginStep === "email" ? "Continue" : "Sign In";

    const isPrimaryDisabled =
        isFormBusy || (loginStep === "otp" && !isOtpComplete);

    const resendCooldownLabel =
        resendCooldownSec > 0
            ? `Resend code in ${String(Math.floor(resendCooldownSec / 60)).padStart(2, "0")}:${String(
                  resendCooldownSec % 60
              ).padStart(2, "0")}`
            : "Resend code";

    return {
        loginStep,
        email,
        setEmail,
        otp,
        handleOtpChange,
        otpFieldKey,
        formErrors,
        isPrimarySubmitting,
        isResendingOtp,
        isFormBusy,
        isPrimaryDisabled,
        resendCooldownSec,
        resendCooldownLabel,
        clearFieldError,
        handleSignInWithGoogle,
        handleBackToEmail,
        handleResendOtp,
        handlePrimaryAction,
        handleKeyDown,
        subheadingText,
        primaryButtonLabel,
    };
};
